/*
 * NDT/VT-pöytäkirjan liite pöytäkirjanumeroon (T562, migraatio 233; Toni 12.10.2026 «OIKEIN»: liite kuuluu numeroon, ei hitsiin).
 *
 * Yksi komponentti NDT-lomakkeeseen ja VT-joukkokuittauksen ikkunaan. Numero tulee lomakkeelta; hitsi antaa kohteen.
 *  - ilman numeroa: liitettä ei voi lisätä, ja syy sanotaan («Anna pöytäkirjan numero …»)
 *  - numerolla on jo liite: 📎 n ja liitteet (Liitteet, kohde 'poytakirja') — ei pyydetä uudestaan
 *  - muuten: «Lisää pöytäkirja (kuva/PDF)» — luo pöytäkirjarivin (idempotentti) ja lähettää tiedostot sille
 * Poisto arkistoi (palvelin), joten nappi sanoo «Arkistoi».
 */
import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "./api";
import { t, virheTeksti } from "./kieli";
import Liitteet, { lueBase64 } from "./Liitteet";

export function PoytakirjaLiite({ hitsiId, numero }: { hitsiId: number; numero: string }) {
  const [tila, setTila] = useState<{ id: number | null; liitteita: number } | null>(null);
  const [auki, setAuki] = useState(false);
  const [lahetetaan, setLahetetaan] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const syote = useRef<HTMLInputElement>(null);
  const nro = numero.trim();

  useEffect(() => {
    setTila(null); setVirhe(null);
    if (!nro) return;
    let peruttu = false;
    const ajastin = setTimeout(() => {
      api.haePoytakirja(hitsiId, nro).then((p) => { if (!peruttu) setTila({ id: p.id, liitteita: p.liitteita }); })
        .catch((e) => { if (!peruttu) setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); });
    }, 400);
    return () => { peruttu = true; clearTimeout(ajastin); };
  }, [hitsiId, nro]);

  async function laheta(tiedostot: FileList | null) {
    if (!tiedostot?.length) return;
    setLahetetaan(true); setVirhe(null);
    try {
      const p = await api.luoPoytakirja(hitsiId, nro);
      for (const f of Array.from(tiedostot)) {
        await api.lisaaLiite({ kohde_taulu: "poytakirja", kohde_id: p.id, nimi: f.name, sisalto: await lueBase64(f) });
      }
      const uusi = await api.haePoytakirja(hitsiId, nro);
      setTila({ id: uusi.id, liitteita: uusi.liitteita }); setAuki(true);
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); }
    finally { setLahetetaan(false); if (syote.current) syote.current.value = ""; }
  }

  return (
    <div className="poytakirja-liite" data-poytakirja-liite={nro || ""}>
      <span className="poytakirja-liite__otsikko">{t("ptk.otsikko")}</span>
      {!nro ? (
        <p className="ikkuna__vihje" data-poytakirja-ei-numeroa="">{t("ptk.anna_numero")}</p>
      ) : tila && tila.id !== null && tila.liitteita > 0 ? (
        <>
          <button type="button" className="tekstilinkki" onClick={() => setAuki(!auki)} data-poytakirja-liitteita={tila.liitteita}>
            📎 {t("ptk.liitteita", { n: tila.liitteita, nro })}</button>
          {auki && <Liitteet kohdeTaulu="poytakirja" kohdeId={tila.id} saaMuokata otsikko={t("ptk.otsikko")}
                             lisaaTeksti={t("ptk.lisaa_sivu")} poistaTeksti={t("ptk.arkistoi")}
                             muuttui={() => api.haePoytakirja(hitsiId, nro).then((p) => setTila({ id: p.id, liitteita: p.liitteita }))} />}
        </>
      ) : (
        <>
          <button type="button" className="nappi nappi--kevyt" disabled={lahetetaan || !tila} onClick={() => syote.current?.click()}
                  data-poytakirja-lisaa="">{lahetetaan ? t("liite.lahetetaan") : t("ptk.lisaa")}</button>
          <input hidden ref={syote} type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
                 onChange={(e) => void laheta(e.target.files)} data-poytakirja-tiedosto="" />
        </>
      )}
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
    </div>
  );
}
